import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  HotelsRawEventName,
  useEmitCoreHotelsRawEventOnce,
} from "@b2bportal/core-hotels";
import {
  type GetElementClassNameFn,
  IconNameEnum,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CommonRawEventName,
  TrackedLoadedPageName,
  useEventTracking,
  useEmitCoreCommonEventBeforeLoad,
} from "@b2bportal/core-tracking";
import {
  Button,
  ButtonTypeEnum,
  Dialog,
  DialogContent,
  Icon,
  PriceDisplay,
} from "@b2bportal/core-ui";
import type { HotelItinerary } from "@b2bportal/lodging-api";
import {
  type Copy,
  type LodgingCancelScenario,
  LodgingCancelScenarioEnum,
} from "@b2bportal/lodging-cancel-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TRIPS_PATH } from "@overrides/utilities";
import { DialogClose, DialogTrigger, Page } from "@tenant/ui";
import { PageLoadingScreen } from "@tenant/ui";
import clsx from "clsx";
import { HotelCancelCard } from "./components/HotelCancelCard";
import {
  type SelfServeHotelCancelPageProps,
  SelfServeHotelCancelStepEnum,
} from "./hooks";

import styles from "./styles.module.scss";
import { ActionButton } from "@/components";
import { ContactSupportDialogContent } from "@/components/ContactSupportDialog";
import { useHotelBookingSupportId } from "@/overrides/core-hotels/HotelReservationPage/hooks/useHotelBookingSupportId";
import { defaultPointsExchange } from "@/utils/points";
import { getPriceValueFromString } from "@/utils/pricing";
import { getReservationPayments } from "@/utils/trips";

const getRefundedAmountsForPartialRefunds = (
  refundedAmount: number,
  fiatPaymentAmount: { amount: number } | null
) => {
  if (!fiatPaymentAmount) {
    return {
      fiat: 0,
      rewards: 0,
    };
  }

  const cashToRefund = Math.min(refundedAmount, fiatPaymentAmount.amount);
  if (!fiatPaymentAmount) {
    return {
      fiat: 0,
      rewards: defaultPointsExchange.getPointsRequiredForFiat({
        value: cashToRefund,
      }),
    };
  }

  const fiatAmountRefundedInPoints = refundedAmount - cashToRefund;
  return {
    fiat: cashToRefund,
    rewards: Math.max(
      defaultPointsExchange.getPointsRequiredForFiat({
        value: fiatAmountRefundedInPoints,
      }),
      0
    ),
  };
};

const getRefundAmountNumber = (
  cancelScenario: LodgingCancelScenario,
  reservation: HotelItinerary
): {
  fiat: number;
  rewards: number;
} => {
  const reservationPayments = getReservationPayments({ reservation });
  if (!cancelScenario) {
    return {
      fiat: 0,
      rewards: 0,
    };
  }

  if (
    cancelScenario.LodgingCancelScenario ===
    LodgingCancelScenarioEnum.PartiallyRefundable
  ) {
    const refundedAmountInFiat = getPriceValueFromString(
      cancelScenario.refundAmount
    );
    return getRefundedAmountsForPartialRefunds(
      refundedAmountInFiat,
      reservationPayments.card
    );
  }

  if (
    cancelScenario.LodgingCancelScenario ===
    LodgingCancelScenarioEnum.CfarRefundable
  ) {
    const refundedAmountInFiat = cancelScenario.refundAmount.fiat.value;
    return getRefundedAmountsForPartialRefunds(
      refundedAmountInFiat,
      reservationPayments.card
    );
  }

  if (
    cancelScenario.LodgingCancelScenario ===
    LodgingCancelScenarioEnum.FullyRefundable
  ) {
    return {
      fiat: reservationPayments.card?.amount ?? 0,
      rewards: reservationPayments.rewards ?? 0,
    };
  }
};

const calculateRefundAmount = (
  cancelScenario: LodgingCancelScenario,
  reservation: HotelItinerary
) => {
  return getRefundAmountNumber(cancelScenario, reservation);
};

export const SelfServeHotelCancelPageView = (
  props: SelfServeHotelCancelPageProps
) => {
  const [block, cn] = useModuleBEM(styles, "SelfServeHotelCancelPage");

  const { t } = useI18nContext();

  const { context, handlers, text } = props;

  const { setGlobalAttribute } = useEventTracking();

  if (context.cancelScenario && context.hotelReservation) {
    const refundedAmount = calculateRefundAmount(
      context.cancelScenario,
      context.hotelReservation
    );

    setGlobalAttribute("cancelledReservationRefundAmount", refundedAmount);

    setGlobalAttribute(
      "cancelledReservationId",
      context.hotelReservation.reservation.reservationId
    );
  }

  useEmitCoreHotelsRawEventOnce(
    {
      name: HotelsRawEventName.HotelCancelReservationSuccessViewed,
      properties: {
        cancel_scenario: context.cancelScenario?.LodgingCancelScenario,
      },
    },
    context.activeStep === SelfServeHotelCancelStepEnum.success &&
      Boolean(context.cancelScenario)
  );

  const showConfirmCancelButton = context.canSelfCancel || context.isFulfilling;

  useEmitCoreHotelsRawEventOnce(
    {
      name: HotelsRawEventName.HotelCancelReservationComponentViewed,
      properties: {
        cancel_scenario: context.cancelScenario?.LodgingCancelScenario,
      },
    },
    showConfirmCancelButton && Boolean(context.cancelScenario)
  );

  const { supportId, isLoading, fetchBookingSupportId, error } =
    useHotelBookingSupportId();

  useEffect(() => {
    fetchBookingSupportId(context.hotelReservation.reservation.reservationId);
  }, [
    context.hotelReservation.reservation.reservationId,
    fetchBookingSupportId,
  ]);

  return (
    <Page
      className={clsx(
        block,
        props.className,
        cn("page-loading", {
          loading: context.isLoading,
        })
      )}
      trackingName={TrackedLoadedPageName.HotelCancellation}
    >
      <div className={cn("page-container")}>
        <div className={cn("page-content")}>
          <PageLoadingScreen
            className={cn("loading")}
            isLoading={
              (context.isLoading || context.isFulfilling) &&
              context.activeStep !== SelfServeHotelCancelStepEnum.error
            }
            text={text.loading}
          />
          {context.hotelReservation && (
            <div className={cn("back-button-container")}>
              <Link
                to={`${TRIPS_PATH}/${context.hotelReservation.reservation.reservationBookingId}`}
                className={cn("back-button")}
              >
                <Icon
                  iconName={IconNameEnum.backIcon}
                  className={cn("back-button-icon")}
                />{" "}
                {t("hotels.selfServe.cancel.backToTripDetails")}
              </Link>
            </div>
          )}
          <h1 className={cn("title")}>{text.title}</h1>
          {context.isReady && (
            <>
              <CancelScenarioInfo
                cancelScenario={context.cancelScenario}
                cn={cn}
              />
              {context.activeStep === SelfServeHotelCancelStepEnum.support && (
                <Dialog>
                  <DialogTrigger className={cn("manage-button")}>
                    {t("hotels.bookingConfirmation.contactSupport")}
                  </DialogTrigger>
                  <DialogContent
                    className={cn("contact-support-dialog")}
                    title={t(
                      "hotels.bookingConfirmation.contactSupportDialog.title"
                    )}
                    bottomFixedContent={
                      <DialogClose>
                        <ActionButton
                          message={t(
                            "hotels.bookingConfirmation.contactSupportDialog.closeButton"
                          )}
                        />
                      </DialogClose>
                    }
                  >
                    <ContactSupportDialogContent supportId={supportId} />
                    <div className={cn("priority-id-section")}>
                      <div className={cn("priority-id-label")}>
                        {t(
                          "hotels.bookingConfirmation.contactSupportDialog.priorityIdLabel"
                        )}
                      </div>
                      <div className={cn("priority-id-value")}>
                        {isLoading
                          ? t(
                              "hotels.bookingConfirmation.contactSupportDialog.loadingPriorityId"
                            )
                          : supportId}
                        {!isLoading && !supportId && error && (
                          <div className={cn("error-message")}>
                            {t(
                              "hotels.bookingConfirmation.contactSupportDialog.priorityIdError"
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
              {context.hotelReservation && (
                <HotelCancelCard
                  hotelReservation={context.hotelReservation}
                  cancelScenario={context.cancelScenario}
                  refundAmount={calculateRefundAmount(
                    context.cancelScenario,
                    context.hotelReservation
                  )}
                />
              )}
              <CancelConfirmationInfo
                cancelScenario={context.cancelScenario}
                cn={cn}
              />
              {showConfirmCancelButton && context.hotelReservation && (
                <div className={cn("action-buttons")}>
                  <Button
                    buttonType={ButtonTypeEnum.Primary}
                    className={cn("confirm-cta")}
                    onClick={handlers.confirmCancel}
                    disabled={
                      context.cancelScenario == null || context.isFulfilling
                    }
                  >
                    {text.confirmCTA}
                  </Button>
                  <Link
                    to={`${TRIPS_PATH}/${context.hotelReservation.reservation.reservationBookingId}`}
                    className={cn("keep-booking-link")}
                  >
                    <Button
                      buttonType={ButtonTypeEnum.Secondary}
                      className={cn("keep-booking-cta")}
                    >
                      {t("hotels.selfServe.cancel.keepBookingCTA")}
                    </Button>
                  </Link>
                </div>
              )}
            </>
          )}
          <CancelModal
            open={context.activeStep === SelfServeHotelCancelStepEnum.success}
            title={t("hotels.selfServe.cancel.successTitle")}
          >
            <div className={cn("success-modal-contents")}>
              <p>{t("hotels.selfServe.cancel.successMessage")}</p>
            </div>
          </CancelModal>
          <CancelModal
            open={context.activeStep === SelfServeHotelCancelStepEnum.error}
            title={t("hotels.selfServe.cancel.errorTitle")}
          >
            {text.errorMessage && <p>{text.errorMessage}</p>}
          </CancelModal>
        </div>
      </div>
    </Page>
  );
};

const CancelScenarioInfo = ({
  cancelScenario,
  cn,
}: {
  cancelScenario: LodgingCancelScenario;
  cn: GetElementClassNameFn;
}) => {
  const { t } = useI18nContext();

  switch (cancelScenario.LodgingCancelScenario) {
    case LodgingCancelScenarioEnum.Canceled:
      return (
        <div className={cn("cancel-scenario-info")}>
          <p>{t("hotels.selfServe.cancel.alreadyCanceled")}</p>
        </div>
      );

    case LodgingCancelScenarioEnum.NonRefundable:
    case LodgingCancelScenarioEnum.FullyRefundable:
      return (
        <CancelCopy
          copy={cancelScenario.cancelCopy}
          showTitle={false}
          cn={cn}
        />
      );

    case LodgingCancelScenarioEnum.PartiallyRefundable:
      return (
        <>
          <CancelCopy
            copy={cancelScenario.cancelCopy}
            showTitle={false}
            cn={cn}
          />
          <div>
            {t("trips.cancel.refundAmount")}: {cancelScenario.refundAmount}
          </div>
        </>
      );
    case LodgingCancelScenarioEnum.CfarRefundable:
      return (
        <>
          <CancelCopy
            copy={cancelScenario.cancelCopy}
            showTitle={false}
            cn={cn}
          />
          <div>
            <PriceDisplay prices={cancelScenario.refundAmount} />
          </div>
        </>
      );

    case LodgingCancelScenarioEnum.OutOfPolicy:
    case LodgingCancelScenarioEnum.ContactCustomerService:
    case LodgingCancelScenarioEnum.PastCheckInDate:
      return (
        <CancelCopy
          showTitle={false}
          copy={cancelScenario.customerServiceCopy}
          cn={cn}
        />
      );
  }
};

const CancelConfirmationInfo = ({
  cancelScenario,
  cn,
}: {
  cancelScenario: LodgingCancelScenario;
  cn: GetElementClassNameFn;
}) => {
  switch (cancelScenario.LodgingCancelScenario) {
    case LodgingCancelScenarioEnum.FullyRefundable:
    case LodgingCancelScenarioEnum.PartiallyRefundable:
      return <CancellationBasicInformation />;

    default:
      return null;
  }
};

const CancelCopy = ({
  showTitle,
  copy,
  className,
  cn,
}: {
  copy: Copy;
  showTitle: boolean;
  className?: string;
  cn: GetElementClassNameFn;
}) => {
  return (
    <div className={clsx(className, cn("cancel-copy"))}>
      {copy.title && showTitle && (
        <h4 className={cn("cancel-copy-title")}>{copy.title}</h4>
      )}
      {copy.body.map((p, i) => (
        <p
          className={cn("cancel-copy-body")}
          key={i}
          dangerouslySetInnerHTML={{ __html: p }}
        />
      ))}
      {copy.importantInfo.map((p, i) => (
        <p
          className={cn("cancel-copy-important")}
          key={i}
          dangerouslySetInnerHTML={{ __html: p }}
        />
      ))}
      {copy.informativeSection && (
        <div className={cn("cancel-copy-informative-section")}>
          <h5 className={cn("cancel-copy-informative-section-title")}>
            {copy.informativeSection?.title}
          </h5>
          {copy.informativeSection.body.map((p, i) => (
            <p
              className={cn("cancel-copy-informative-section-p")}
              key={i}
              dangerouslySetInnerHTML={{ __html: p }}
            />
          ))}
        </div>
      )}

      {copy.disclaimer && (
        <p className={cn("cancel-copy-disclaimer")}>{copy.disclaimer}</p>
      )}
    </div>
  );
};

const CancelModal = ({ children, open, title }) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "SelfServeHotelCancelPage");
  return (
    <Dialog open={open} modal={true}>
      <DialogContent showCloseButton={false} title={title}>
        <div className={cn("modal-contents")}>{children}</div>
        <div className={cn("modal-action-container")}>
          <Link className={cn("done-link")} to={TRIPS_PATH}>
            {t("done")}
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CancellationBasicInformation = () => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "SelfServeHotelCancelPage");
  return (
    <div className={cn("cancel-basic-cancellation-rules")}>
      <h1 className={cn("cancel-basic-cancellation-rules-title")}>
        {t("hotels.selfServe.cancel.cancelCopyTitle")}
      </h1>
      <ol className={cn("cancel-basic-cancellation-rules-list")}>
        <li>{t("hotels.selfServe.cancel.basicCancellationRules.rule1")}</li>
        <li>{t("hotels.selfServe.cancel.basicCancellationRules.rule2")}</li>
        <li>{t("hotels.selfServe.cancel.basicCancellationRules.rule3")}</li>
        <li>{t("hotels.selfServe.cancel.basicCancellationRules.rule4")}</li>
      </ol>
    </div>
  );
};
